import {
  Box,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogTitle,
  InputAdornment,
  Stack,
  styled,
  TextField,
  Typography,
  DialogContent,
  useTheme,
} from '@mui/material'
import List, { ListRenderer, SortDirection } from 'components/List'
import SearchIcon from '@mui/icons-material/Search'
import { ClearSearch } from 'components/JelledIcons'
import { useFetchOrgsList } from 'hooks/use-admin'
import OrganizationLogo from 'components/OrganizationLogo'
import { useMemo, useRef, useState } from 'react'
import debounce from 'lodash.debounce'
import { OrganizationFull, OrgType } from 'api/model'
import { useSetRecoilState } from 'recoil'
import { toastAtom } from 'state'
import { adminSearchOrgs, adminDeleteOrg } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { format, compareAsc } from 'date-fns'
import AdminAddOrganization from './AdminAddOrganization'
import AdminOrganizationSidebar from './AdminOrganizationSidebar'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { getColorForDarkMode, isFieldEmpty } from 'utils'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import MobileOverlay from 'components/MobileOverlay'
import { COLORS } from 'app-constants'
import { useActiveOrganization } from 'hooks/use-organization'

enum SortableColumn {
  Name = 1,
  Created = 4,
}

const AccountManagement = () => {
  const [activeOrganization] = useActiveOrganization()
  const theme = useTheme()

  const isMobile = useCheckMobileScreen()

  const { isLoading, adminOrgs, setAdminOrgs } = useFetchOrgsList()

  const setToast = useSetRecoilState(toastAtom)

  const [searchQuery, setSearchQuery] = useState('')
  const pendingQuery = useRef('')
  const [searchResults, setSearchResults] = useState<OrganizationFull[]>([])

  const debounceQueryUpdate = useRef(
    debounce(async (query: string) => {
      if (!query) {
        setSearchResults([])
        return
      }

      try {
        const results = await adminSearchOrgs({ q: query })

        if (query !== pendingQuery.current) {
          return
        }

        setSearchResults(results.data.items)
      } catch (Error) {
        setToast({ title: 'Search failed', severity: 'error' })
      }
    }, 500)
  ).current

  const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)

    pendingQuery.current = event.target.value
    debounceQueryUpdate(event.target.value)
  }

  const orgs = useMemo(() => {
    const list = searchResults.length > 0 ? searchResults : adminOrgs
    return list.filter((of) => of.type === OrgType.customer)
  }, [adminOrgs, searchResults])

  const [isAddAdminOpen, setIsAddAdminOpen] = useState(false)
  const onFinishedAdd = (newOrg?: OrganizationFull) => {
    setIsAddAdminOpen(false)
    if (newOrg) {
      setAdminOrgs((currentOrgs) => {
        return [...currentOrgs, newOrg]
      })
    }
  }

  const [pendingDeleteOrgId, setPendingDeleteOrgId] = useState<string | null>(null)
  const [deleteSubmitCount, setDeleteSubmitCount] = useState(0)
  const [isDeletingOrg, setIsDeletingOrg] = useState(false)

  const [deletionReason, setDeletionReason] = useState<string>('')
  const onDeletionReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDeletionReason(event.target.value)
  }

  const onDeleteClicked = (org: OrganizationFull) => {
    setPendingDeleteOrgId(org.id)
  }

  const onDeleteOrgConfirmed = async () => {
    setDeleteSubmitCount((submitCount) => submitCount + 1)
    if (!pendingDeleteOrgId || isFieldEmpty(deletionReason) || !activeOrganization) {
      return
    }

    setIsDeletingOrg(true)
    try {
      await adminDeleteOrg(activeOrganization, { reason: deletionReason }, { delete_org_id: pendingDeleteOrgId })

      setAdminOrgs((currentOrgs) => {
        return currentOrgs.filter((org) => {
          return org.id !== pendingDeleteOrgId
        })
      })
      setToast({ title: 'Organization deleted successfully', severity: 'success' })
    } catch (error) {
      setToast({ title: 'Organization delete failed', severity: 'error' })
    } finally {
      setPendingDeleteOrgId(null)
      setIsDeletingOrg(false)
      setSelectedOrgId(null)
      setDeletionReason('')
      setDeleteSubmitCount(0)
    }
  }

  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)
  const [sortedColumnIndex, setSortedColumnIndex] = useState<number>(SortableColumn.Name)
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.Ascending)

  const getSortedOrgs = () => {
    switch (sortedColumnIndex) {
      case SortableColumn.Name:
        return orgs.sort(function (a, b) {
          if (sortDirection === SortDirection.Ascending) {
            return a.name.localeCompare(b.name)
          }
          return b.name.localeCompare(a.name)
        })
      case SortableColumn.Created:
        return orgs.sort(function (a, b) {
          const dateA = new Date(a.date_created ?? '')
          const dateB = new Date(b.date_created ?? '')

          if (sortDirection === SortDirection.Ascending) {
            return compareAsc(dateA, dateB)
          }

          return compareAsc(dateB, dateA)
        })
      default:
        return orgs
    }
  }

  const listRenderer: ListRenderer = {
    rows: getSortedOrgs().map((org) => {
      return {
        key: org.id,
        columnWidth: (column) => {
          if (column === 0) {
            return 64
          }

          if (column === 6) {
            return 64
          }

          return undefined
        },
        columnEntry: (column) => {
          switch (column) {
            case 0:
              return (
                <Box py={1}>
                  <OrganizationLogo organization={org} />
                </Box>
              )
            case 1:
              return <Typography variant="h6">{org.name}</Typography>
            case 2:
              return <Typography variant="h6">{org.status}</Typography>
            case 3:
              return <Typography variant="h6">{org.email_domain}</Typography>
            case 4:
              return <Typography variant="h6">{org.subscription.product_name}</Typography>
            case 5:
              return format(new Date(org.date_created ?? ''), `MMM d, y`)
            case 6:
              return <Typography variant="h6">{org.deletion_requested ? 'Deletion requested' : 'N/A'}</Typography>
            case 7:
              if (isMobile) {
                return (
                  <IconButton color="secondary" size="small" onClick={() => setSelectedOrgId(org.id)}>
                    <ArrowForwardIcon />
                  </IconButton>
                )
              }

              return (
                <HoverVisibleStack alignItems="flex-end">
                  <IconButton color="secondary" size="small" onClick={() => setSelectedOrgId(org.id)}>
                    <ArrowForwardIcon />
                  </IconButton>
                </HoverVisibleStack>
              )
          }
        },
      }
    }),
    columnCount: 8,
    columnName: (column) => {
      switch (column) {
        case 1:
          return 'Account name'
        case 2:
          return 'Status'
        case 3:
          return 'Domain'
        case 4:
          return 'Product'
        case 5:
          return 'Created'
        case 6:
          return 'Deletion status'
      }

      return null
    },
  }

  const onColumnHeaderClick = (columnIndex: number) => {
    // if we clicked on already sorted column index, should toggle the sort direction
    if (sortedColumnIndex === columnIndex) {
      setSortDirection(sortDirection === SortDirection.Ascending ? SortDirection.Descending : SortDirection.Ascending)
    } else {
      // if clicking on a new column, default direction to ascending
      setSortDirection(SortDirection.Ascending)
    }
    setSortedColumnIndex(columnIndex)
  }

  return (
    <>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isMobile ? 'flex-start' : 'center'}
        mb={2}>
        {!isMobile && (
          <Typography variant="h4" mb={1}>
            Account management
          </Typography>
        )}
        <Button variant="contained" onClick={() => setIsAddAdminOpen(true)}>
          Create new account
        </Button>
      </Stack>

      <TextField
        value={searchQuery}
        onChange={onSearchChange}
        size="small"
        placeholder="Search"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon
                sx={{
                  color: COLORS.GRAY_3,
                }}
              />
            </InputAdornment>
          ),
          endAdornment: (
            <>
              {searchQuery.length > 0 && (
                <InputAdornment position="end">
                  <ClearSearch
                    style={{ cursor: 'pointer', marginRight: '8px' }}
                    onClick={() => {
                      setSearchQuery('')
                    }}
                  />
                </InputAdornment>
              )}
            </>
          ),
        }}
        inputProps={{ sx: { pl: 0 } }}
        sx={{
          mb: 4,
          width: isMobile ? '100%' : '320px',
        }}
      />

      <List
        sortedColumnIndex={sortedColumnIndex}
        sortDirection={sortDirection}
        sortableColumnIndices={[SortableColumn.Created, SortableColumn.Name]}
        onColumnHeaderClick={onColumnHeaderClick}
        renderer={listRenderer}
        isLoading={isLoading}
      />

      {isAddAdminOpen && <AdminAddOrganization onFinished={onFinishedAdd} />}

      {isMobile && !!selectedOrgId ? (
        <MobileOverlay closeButtonColor="#222222" onClose={() => setSelectedOrgId(null)}>
          <AdminOrganizationSidebar
            organizationId={selectedOrgId}
            open={!!selectedOrgId}
            onDeleteClicked={onDeleteClicked}
            onClose={() => setSelectedOrgId(null)}
          />
        </MobileOverlay>
      ) : (
        <AdminOrganizationSidebar
          organizationId={selectedOrgId}
          open={!!selectedOrgId}
          onDeleteClicked={onDeleteClicked}
          onClose={() => setSelectedOrgId(null)}
        />
      )}

      <Dialog open={!!pendingDeleteOrgId}>
        <DialogTitle>Are you sure you want to delete this Organization?</DialogTitle>
        <DialogContent>
          <Stack mt={3}>
            <TextField
              label="Deletion reason"
              placeholder="Enter a good reason"
              value={deletionReason}
              onChange={onDeletionReasonChange}
              disabled={isDeletingOrg}
              error={deleteSubmitCount > 0 && isFieldEmpty(deletionReason)}
              helperText={deleteSubmitCount > 0 && isFieldEmpty(deletionReason) && 'You must enter deletion reason'}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPendingDeleteOrgId(null)
            }}
            sx={{
              color: getColorForDarkMode(theme),
            }}
            disabled={isDeletingOrg}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={onDeleteOrgConfirmed} disabled={isDeletingOrg}>
            Yes, delete it
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const HoverVisibleStack = styled(Stack)`
  visibility: hidden;

  .isHovering & {
    visibility: visible;
  }
`

export default AccountManagement
